import React, { useState } from 'react';
import { API_BASE_URL } from '../../../services/apiClient';

interface EndpointResult {
  endpoint: string;
  status: 'idle' | 'loading' | 'ok' | 'error';
  response?: unknown;
  error?: string;
  ms?: number;
}

const ENDPOINTS = [
  { label: 'Health Check', path: '/health', method: 'GET', base: 'root' },
  { label: 'Root API', path: '/', method: 'GET', base: 'root' },
  { label: 'Sorteos', path: '/sorteos', method: 'GET', base: 'api' },
  { label: 'Usuarios', path: '/usuarios', method: 'GET', base: 'api' },
  { label: 'Reglas', path: '/reglas', method: 'GET', base: 'api' },
];

function getBaseUrl(): string {
  return API_BASE_URL.replace('/api', '');
}

export const TestPage: React.FC = () => {
  const [results, setResults] = useState<Record<string, EndpointResult>>({});
  const [globalStatus, setGlobalStatus] = useState<'idle' | 'loading' | 'ok' | 'error'>('idle');
  const token = localStorage.getItem('tiempos_auth_token');

  async function testEndpoint(endpoint: typeof ENDPOINTS[0]) {
    const url =
      endpoint.base === 'root'
        ? `${getBaseUrl()}${endpoint.path}`
        : `${API_BASE_URL}${endpoint.path}`;

    setResults(prev => ({
      ...prev,
      [endpoint.label]: { endpoint: url, status: 'loading' },
    }));

    const start = Date.now();
    try {
      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const ms = Date.now() - start;
      const data = await res.json().catch(() => null);
      setResults(prev => ({
        ...prev,
        [endpoint.label]: {
          endpoint: url,
          status: res.ok ? 'ok' : 'error',
          response: data,
          ms,
        },
      }));
    } catch (err: unknown) {
      setResults(prev => ({
        ...prev,
        [endpoint.label]: {
          endpoint: url,
          status: 'error',
          error: err instanceof Error ? err.message : String(err),
          ms: Date.now() - start,
        },
      }));
    }
  }

  async function testAll() {
    setGlobalStatus('loading');
    await Promise.all(ENDPOINTS.map(testEndpoint));
    setGlobalStatus('ok');
  }

  function statusBadge(status: EndpointResult['status']) {
    const map = {
      idle: 'bg-gray-200 text-gray-600',
      loading: 'bg-yellow-100 text-yellow-700 animate-pulse',
      ok: 'bg-green-100 text-green-700',
      error: 'bg-red-100 text-red-700',
    };
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${map[status]}`}>
        {status.toUpperCase()}
      </span>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow p-6">
          <h1 className="text-2xl font-bold text-gray-800 mb-1">Pagina de Pruebas</h1>
          <p className="text-gray-500 text-sm">Verifica la conexion entre frontend y backend</p>
        </div>

        {/* Config Info */}
        <div className="bg-white rounded-lg shadow p-6 space-y-3">
          <h2 className="font-semibold text-gray-700">Configuracion de Puertos</h2>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="bg-blue-50 rounded p-3">
              <p className="text-blue-600 font-medium">Frontend (Vite)</p>
              <p className="text-blue-800 font-mono">http://localhost:3000</p>
            </div>
            <div className="bg-purple-50 rounded p-3">
              <p className="text-purple-600 font-medium">Backend (FastAPI)</p>
              <p className="text-purple-800 font-mono">{getBaseUrl()}</p>
            </div>
          </div>
          <div className="bg-gray-50 rounded p-3 text-sm">
            <p className="text-gray-500 font-medium">API Base URL</p>
            <p className="text-gray-800 font-mono">{API_BASE_URL}</p>
          </div>
          <div className="bg-gray-50 rounded p-3 text-sm">
            <p className="text-gray-500 font-medium">Token JWT</p>
            <p className="text-gray-800 font-mono truncate">
              {token ? `${token.substring(0, 40)}...` : 'No autenticado'}
            </p>
          </div>
        </div>

        {/* Endpoint Tests */}
        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-gray-700">Prueba de Endpoints</h2>
            <button
              onClick={testAll}
              disabled={globalStatus === 'loading'}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition"
            >
              {globalStatus === 'loading' ? 'Probando...' : 'Probar Todos'}
            </button>
          </div>

          <div className="space-y-3">
            {ENDPOINTS.map(ep => {
              const r = results[ep.label];
              return (
                <div key={ep.label} className="border rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm text-gray-700">{ep.label}</span>
                      {r && statusBadge(r.status)}
                      {r?.ms !== undefined && (
                        <span className="text-xs text-gray-400">{r.ms}ms</span>
                      )}
                    </div>
                    <button
                      onClick={() => testEndpoint(ep)}
                      className="text-xs px-3 py-1 border border-gray-300 rounded hover:bg-gray-50 transition"
                    >
                      Probar
                    </button>
                  </div>
                  <p className="text-xs font-mono text-gray-400 truncate">
                    {ep.base === 'root'
                      ? `${getBaseUrl()}${ep.path}`
                      : `${API_BASE_URL}${ep.path}`}
                  </p>
                  {r?.error && (
                    <p className="text-xs text-red-600 mt-1">{r.error}</p>
                  )}
                  {r?.response !== undefined && r.status === 'ok' && (
                    <pre className="text-xs text-green-700 mt-1 bg-green-50 p-2 rounded overflow-x-auto max-h-32">
                      {JSON.stringify(r.response, null, 2)}
                    </pre>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Seed Data */}
        <div className="bg-white rounded-lg shadow p-6 space-y-3">
          <h2 className="font-semibold text-gray-700">Utiles de Desarrollo</h2>
          <div className="flex gap-3">
            <button
              onClick={() => testEndpoint({ label: 'Seed Data', path: '/api/seed', method: 'POST', base: 'root' })}
              className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 transition"
            >
              Seed Sorteos
            </button>
            <button
              onClick={() => testEndpoint({ label: 'Seed Users', path: '/api/seed-users', method: 'POST', base: 'root' })}
              className="px-4 py-2 bg-emerald-500 text-white rounded-lg text-sm font-medium hover:bg-emerald-600 transition"
            >
              Seed Usuarios
            </button>
          </div>
          {results['Seed Data'] && (
            <pre className="text-xs p-2 bg-gray-50 rounded overflow-x-auto max-h-40">
              {JSON.stringify(results['Seed Data'].response ?? results['Seed Data'].error, null, 2)}
            </pre>
          )}
          {results['Seed Users'] && (
            <pre className="text-xs p-2 bg-gray-50 rounded overflow-x-auto max-h-40">
              {JSON.stringify(results['Seed Users'].response ?? results['Seed Users'].error, null, 2)}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
};
